import { StandaloneButtonProps } from '@/components/element/Button';
import { StandaloneIconProps } from '@/components/element/Icon';
import { StandaloneLinkProps } from '@/components/element/Link';
import { cn } from 'lib/utils/cn';
import logger from 'lib/utils/logger';
import { standaloneComponent } from 'modules/dynamic-components';
import { useState } from 'react';

const Icon = standaloneComponent<StandaloneIconProps>('ElementIcon');
const Button = standaloneComponent<StandaloneButtonProps>('ElementButton');
const Link = standaloneComponent<StandaloneLinkProps>('ElementLink');

interface ActiveOffer {
  offerText: string;
  offerCode: string;
  offerLink: string;
  offerLinkText: string;
  offerFooterText: string;
  image: React.ReactNode;
}

interface Props {
  activeOffer: ActiveOffer;
  onClose: () => void;
}

const ElleMemberOffersModal: React.FC<Props> = ({ activeOffer, onClose }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // Close modal when clicking on overlay
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(activeOffer.offerCode);
      setShowTooltip(true);

      // Show "Copied" message to user for two seconds
      setTimeout(() => setShowTooltip(false), 2000);
    } catch (error: any) {
      logger.catch(`Member offers failed to copy code: "${error.message}"`);
      return null;
    }
  };

  return (
    <div
      className={cn('fixed', 'inset-0', 'px-6', 'bg-black', 'bg-opacity-30', 'backdrop-blur-sm', 'z-[200]')}
      onClick={handleOverlayClick}
    >
      <div
        className={cn(
          'relative',
          'top-1/2',
          '-translate-y-1/2',
          'flex',
          'flex-col',
          'items-center',
          'w-full',
          'max-w-96',
          'mx-auto',
          'py-10',
          'px-4',
          'bg-white',
          'text-el-preview-lg',
          'text-center'
        )}
      >
        <div className={cn('absolute', 'top-0', 'right-0', 'p-2', 'cursor-pointer')} onClick={onClose}>
          <Icon name={'close'} options={{ size: 'medium' }} />
        </div>
        <div className={cn('relative', 'w-full')}>{activeOffer.image}</div>
        {activeOffer.offerCode && (
          <div className={cn('flex', 'flex-row', 'items-center', 'my-6', 'py-2', 'px-6', 'bg-el-gray-800')}>
            <span className={cn('mr-4')}>Din kod:</span>
            <span className={cn('mr-4', 'font-medium')}>{activeOffer.offerCode}</span>
            <button className={cn('relative', 'cursor-pointer', 'focus:none')} onClick={handleCopyCode}>
              <Icon name="copy" options={{ size: 'medium' }} />
              <div
                className={cn(
                  'absolute',
                  '-top-9',
                  'left-1/4',
                  'transform',
                  '-translate-x-1/4',
                  'flex',
                  'flex-row',
                  'px-3',
                  'py-1',
                  'text-black',
                  'border',
                  'text-sm',
                  'bg-white',
                  'shadow-md',
                  'transition-opacity',
                  'duration-300',
                  showTooltip ? 'opacity-100' : 'opacity-0',
                  showTooltip ? 'visibility-visible' : 'visibility-hidden'
                )}
              >
                <Icon name="done" options={{ className: cn('mr-1'), size: 'medium' }} />
                <span className={cn('mt-0.5')}>Kopierad!</span>
              </div>
            </button>
          </div>
        )}
        {activeOffer.offerLink && (
          <Link
            href={activeOffer.offerLink}
            target="_blank"
            options={{
              rel: 'nofollow',
              className: cn('flex', 'flex-row', 'items-center', 'mb-4', 'font-medium'),
            }}
            content={
              <>
                {activeOffer.offerLinkText ? activeOffer.offerLinkText : 'Gå till event'}
                <Icon name="chevronRight" options={{ size: 'medium' }} />
              </>
            }
          />
        )}
        {activeOffer.offerFooterText && <p className={cn('text-el-preview-sm')}>{activeOffer.offerFooterText}</p>}
      </div>
    </div>
  );
};

export default ElleMemberOffersModal;
